<template>
    <Head title="Reflections" />
    <BaseLayout class="bg-valence-cream-50">
        <div class="flex flex-col gap-7">
            <div class="flex flex-col gap-3.5">
                <h1 class="display-serif text-valence-stone-900">Reflections</h1>
                <hr class="border-t border-valence-cream-300" />
            </div>

            <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
            <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

            <div v-else-if="hub" class="flex flex-col gap-5 lg:flex-row lg:gap-10 lg:items-start">
                <aside class="shrink-0 flex flex-col gap-3 lg:gap-0">
                    <ReflectionsSideNav :active="activeTab" :show-admin="hub.is_assessment_admin" :tabs="hub.tabs" @select="onTabChange" />
                    <a
                        v-if="hub.team_results_enabled"
                        href="/reflections/team"
                        class="base-btn-cream base-btn-square base-btn-sm w-full inline-flex items-center justify-center gap-2 lg:mt-4">
                        <i class="bi bi-people" aria-hidden="true"></i>
                        Team results
                    </a>
                </aside>

                <div class="flex-1 min-w-0 overflow-hidden">
                    <Transition :name="transitionName" mode="out-in">
                        <div :key="viewKey" class="flex flex-col gap-5">
                            <template v-if="activeView.kind === 'take-self'">
                                <SelfAssessTaker
                                    :cycle-id="activeView.cycleId"
                                    :title="activeView.title"
                                    :accent-color="activeView.accentColor"
                                    :accent-surface-color="activeView.accentSurfaceColor"
                                    :step="activeView.step"
                                    :closes-at="activeView.closesAt"
                                    :is-sixty="activeView.isSixty"
                                    @back="onExitInlineView"
                                    @submitted="onSelfSubmitted" />
                            </template>
                            <template v-else-if="activeView.kind === 'self-complete'">
                                <SelfAssessCompletePanel :cycle-id="activeView.cycleId" @back="onTabChange('upcoming')" @view="onTabChange('sixty')" />
                            </template>
                            <template v-else-if="activeView.kind === 'results'">
                                <ResultsPanel :cycle-id="activeView.cycleId" :show-suggested-goals="show_suggested_goals" @back="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'start-360'">
                                <Start360Panel :mode="activeView.mode" @back="onExitInlineView" @created="onCycleCreated" />
                            </template>
                            <template v-else-if="activeView.kind === 'nominate'">
                                <Nominate360Panel :cycle-id="activeView.cycleId" @back="onExitInlineView" @launched="onCycleLaunched" />
                            </template>
                            <template v-else-if="activeView.kind === 'launching'">
                                <Launch360Animation @done="onLaunchAnimationDone" />
                            </template>
                            <template v-else-if="activeView.kind === 'launched'">
                                <Launched360Panel
                                    :self-assess-enabled="activeView.selfAssessEnabled"
                                    :closes-at="activeView.closesAt"
                                    @start="onStartSelfAfterLaunch"
                                    @done="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'new-campaign'">
                                <AdminNewCampaignPanel :templates="activeView.templates" @back="onExitInlineView" @created="onCampaignCreated" />
                            </template>
                            <template v-else-if="activeView.kind === 'admin-campaign-results'">
                                <AdminCampaignResultsPanel :campaign-id="activeView.campaignId" @back="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'admin-settings'">
                                <AdminSettingsPanel @back="onExitInlineView" />
                            </template>
                            <template v-else-if="activeView.kind === 'give-feedback'">
                                <TeamFeedbackPanel
                                    :invite-token="activeView.inviteToken"
                                    :subject-first-name="activeView.subjectFirstName"
                                    @back="onFeedbackFinished"
                                    @submitted="refreshHub" />
                            </template>
                            <UpcomingTab
                                v-else-if="activeTab === 'upcoming'"
                                :items="upcomingItems"
                                :tabs="hub.tabs"
                                @start-self="onStartSelf"
                                @view-cycle="onViewCycle"
                                @nominate="onNominate"
                                @start-from-campaign="onStartFromCampaign"
                                @open-tab="onTabChange"
                                @give-feedback="onGiveFeedbackFromUpcoming"
                                @new-360="onStart360"
                                @start-blank="onStartSelfBlank" />
                            <SixtiesTab
                                v-else-if="activeTab === 'sixty'"
                                :active-cycles="hub.active_360s ?? []"
                                :past-cycles="hub.past_360s ?? []"
                                :pending-tasks="sixtyTasks"
                                @remind="onRemind"
                                @wrap-up="onWrapUp"
                                @invite-more="onNominate"
                                @nominate="onNominate"
                                @start-from-campaign="onStartFromCampaign"
                                @view-cycle="onViewCycle"
                                @open-self="onOpenSelfFrom360"
                                @deleted="refreshHub"
                                @new-360="onStart360" />
                            <SelfReflectionTab
                                v-else-if="activeTab === 'self'"
                                :pending-tasks="selfTasks"
                                :past-reflections="hub.past_self_reflections ?? []"
                                @start="onStartSelf"
                                @view-cycle="onViewCycle"
                                @start-from-campaign="onStartFromCampaign"
                                @deleted="refreshHub"
                                @start-blank="onStartSelfBlank" />
                            <TeamReflectionsTab v-else-if="activeTab === 'team'" :requests="hub.team_reflection_requests ?? []" @give-feedback="onGiveFeedback" />
                            <AdminTab
                                v-else-if="activeTab === 'admin' && hub.is_assessment_admin"
                                @new-campaign="onNewCampaign"
                                @view-campaign="onViewCampaign"
                                @settings="onAdminSettings" />
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, HubPayload, TeamReflectionRequest } from "~vue/api/generated/reflections";
import type { TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import AdminCampaignResultsPanel from "~vue/pages/reflections/AdminCampaignResultsPanel.vue";
import AdminNewCampaignPanel from "~vue/pages/reflections/AdminNewCampaignPanel.vue";
import AdminSettingsPanel from "~vue/pages/reflections/AdminSettingsPanel.vue";
import AdminTab from "~vue/pages/reflections/AdminTab.vue";
import Launch360Animation from "~vue/pages/reflections/Launch360Animation.vue";
import Launched360Panel from "~vue/pages/reflections/Launched360Panel.vue";
import Nominate360Panel from "~vue/pages/reflections/Nominate360Panel.vue";
import ReflectionsSideNav, { isTabAvailable, type ReflectionsTabKey } from "~vue/pages/reflections/ReflectionsSideNav.vue";
import ResultsPanel from "~vue/pages/reflections/ResultsPanel.vue";
import SelfAssessCompletePanel from "~vue/pages/reflections/SelfAssessCompletePanel.vue";
import SelfAssessTaker from "~vue/pages/reflections/SelfAssessTaker.vue";
import SelfReflectionTab from "~vue/pages/reflections/SelfReflectionTab.vue";
import SixtiesTab from "~vue/pages/reflections/SixtiesTab.vue";
import Start360Panel from "~vue/pages/reflections/Start360Panel.vue";
import TeamFeedbackPanel from "~vue/pages/reflections/TeamFeedbackPanel.vue";
import TeamReflectionsTab from "~vue/pages/reflections/TeamReflectionsTab.vue";
import type { UpcomingItem } from "~vue/pages/reflections/UpcomingTab.vue";
import UpcomingTab from "~vue/pages/reflections/UpcomingTab.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, onUnmounted, ref } from "vue";

export interface Props {
    // Server-computed from the JOURNEYS_PAGE feature flag so we never mount
    // SuggestedGoalsSection for users whose Journeys page isn't enabled (their
    // /suggest-goals call would 403).
    show_suggested_goals?: boolean;
}

const { show_suggested_goals = false } = defineProps<Props>();

// The inline view state — when the user clicks Start check in / View, the
// content pane replaces the list with the appropriate embedded flow. The
// list state (`kind: 'list'`) is the default; the sidebar tab picks WHICH
// list is shown.
type ActiveView =
    | { kind: "list" }
    | {
          kind: "take-self";
          cycleId: string;
          title: string;
          closesAt: string | null;
          accentColor?: string;
          accentSurfaceColor?: string;
          step?: string;
          isSixty?: boolean;
      }
    | { kind: "self-complete"; cycleId: string }
    | { kind: "results"; cycleId: string }
    | { kind: "start-360"; mode: "self" | "360" }
    | { kind: "nominate"; cycleId: string }
    | { kind: "launching"; cycleId: string; title: string; selfAssessEnabled: boolean; closesAt: string | null }
    | { kind: "launched"; cycleId: string; title: string; selfAssessEnabled: boolean; closesAt: string | null }
    | { kind: "new-campaign"; templates: TemplateSummary[] }
    | { kind: "admin-campaign-results"; campaignId: string }
    | { kind: "admin-settings" }
    | { kind: "give-feedback"; inviteToken: string; subjectFirstName: string };

const SIXTY_ACCENT = {
    accentColor: "var(--color-valence-pink-600)",
    accentSurfaceColor: "var(--color-valence-pink-100)",
    isSixty: true,
} as const;

const hub = ref<HubPayload | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const requestedTab = ref<ReflectionsTabKey>(initialTabFromLocation());
// A tab the org has turned off is unreachable even via ?tab=.
const activeTab = computed<ReflectionsTabKey>(() => (isTabAvailable(requestedTab.value, hub.value?.tabs) ? requestedTab.value : "upcoming"));
const activeView = ref<ActiveView>({ kind: "list" });
const transitionName = ref<"hub-forward" | "hub-back">("hub-forward");
// Key that changes whenever the visible view changes so Vue's <Transition
// mode="out-in"> knows to run the leave/enter cycle. Combines tab + kind + id.
const viewKey = computed(() => {
    const view = activeView.value;
    switch (view.kind) {
        case "list":
            return `list:${activeTab.value}`;
        case "start-360":
            return `start-360:${view.mode}`;
        case "new-campaign":
            return "new-campaign";
        case "admin-campaign-results":
            return `admin-campaign-results:${view.campaignId}`;
        case "admin-settings":
            return "admin-settings";
        case "give-feedback":
            return `give-feedback:${view.inviteToken}`;
        default:
            return `${view.kind}:${view.cycleId}`;
    }
});

onMounted(async () => {
    globalThis.addEventListener("popstate", onPopState);
    const { data, error: fetchError } = await useFetch("/v1/reflections/hub").json<HubPayload>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load reflections.";
        return;
    }
    hub.value = data.value;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_HUB_VIEWED, {
        feature_enabled: hub.value?.feature_enabled ?? false,
        in_progress_count: hub.value?.in_progress_cycles.length ?? 0,
    });
});

onUnmounted(() => {
    globalThis.removeEventListener("popstate", onPopState);
});

function initialTabFromLocation(): ReflectionsTabKey {
    if (typeof globalThis.location === "undefined") {
        return "upcoming";
    }
    const params = new URLSearchParams(globalThis.location.search);
    const tab = params.get("tab");
    if (tab === "sixty" || tab === "self" || tab === "team" || tab === "upcoming" || tab === "admin") {
        return tab;
    }
    return "upcoming";
}

function onTabChange(tab: ReflectionsTabKey) {
    if (tab === activeTab.value) {
        // Re-selecting the section you're already in is the way out of an
        // inline take/results panel — otherwise the nav item looks active but
        // does nothing while the user is mid-flow.
        if (activeView.value.kind !== "list") {
            onExitInlineView();
        }
        return;
    }
    // Switching tabs always exits an inline view. Direction is "back" so the
    // transition feels like retreating from the take/results panel.
    transitionName.value = "hub-back";
    activeView.value = { kind: "list" };
    requestedTab.value = tab;
    const url = new URL(globalThis.location.href);
    if (tab === "upcoming") {
        url.searchParams.delete("tab");
    } else {
        url.searchParams.set("tab", tab);
    }
    // pushState (not replaceState) so browser back steps through prior
    // sub-tabs instead of exiting the hub in one press. onPopState below
    // syncs activeTab back from the URL when the user hits back/forward.
    globalThis.history.pushState({ tab }, "", url.toString());
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_HUB_TAB_CHANGED, { tab });
}

function onPopState() {
    const tab = initialTabFromLocation();
    if (tab === activeTab.value) {
        return;
    }
    transitionName.value = "hub-back";
    activeView.value = { kind: "list" };
    requestedTab.value = tab;
}

function onStartSelf(payload: { cycleId: string; title: string; closesAt: string | null }) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "take-self", cycleId: payload.cycleId, title: payload.title, closesAt: payload.closesAt };
}

// The subject completing their self-portion of a 360 gets the 360 pink accent
// so the cycle's visual identity carries through the flow — differentiates from
// self-only Self Reflection cycles, which keep the default warm accent.
function onOpenSelfFrom360(payload: { cycleId: string; title: string; closesAt: string | null }) {
    transitionName.value = "hub-forward";
    activeView.value = {
        kind: "take-self",
        cycleId: payload.cycleId,
        title: payload.title,
        closesAt: payload.closesAt,
        ...SIXTY_ACCENT,
    };
}

function onViewCycle(cycleId: string) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "results", cycleId };
}

function onExitInlineView() {
    transitionName.value = "hub-back";
    activeView.value = { kind: "list" };
    // Refresh so a state change that happened inside the inline view (report
    // generated on ResultsPanel, cycle launched from Nominate, campaign
    // updated on Admin) doesn't leave the list showing stale rows like a
    // "Generating…" card for a report that's already ready.
    void refreshHub();
}

function onStart360() {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "start-360", mode: "360" };
}

function onStartSelfBlank() {
    // Educational card on the Self tab: kick off the standalone
    // "Start a self assessment" flow inline.
    transitionName.value = "hub-forward";
    activeView.value = { kind: "start-360", mode: "self" };
}

function onNominate(cycleId: string) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "nominate", cycleId };
}

function onNewCampaign(templates: TemplateSummary[]) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "new-campaign", templates };
}

function onViewCampaign(campaignId: string) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "admin-campaign-results", campaignId };
}

function onAdminSettings() {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "admin-settings" };
}

function onGiveFeedback(request: TeamReflectionRequest) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "give-feedback", inviteToken: request.invite_token, subjectFirstName: request.subject_first_name };
}

// Same panel as the Team Reflections tab's Give feedback, but sourced from
// an Upcoming row's inviteToken + subjectFirstName rather than a full
// TeamReflectionRequest. Kept as its own handler so payload shapes stay
// clean at each call site.
function onGiveFeedbackFromUpcoming(payload: { inviteToken: string; subjectFirstName: string }) {
    transitionName.value = "hub-forward";
    activeView.value = { kind: "give-feedback", inviteToken: payload.inviteToken, subjectFirstName: payload.subjectFirstName };
}

// Submitting only refreshes the hub data — the panel keeps the inline view for
// its sending animation and "Feedback submitted" state until Done is clicked.
async function onFeedbackFinished() {
    transitionName.value = "hub-back";
    activeView.value = { kind: "list" };
    requestedTab.value = "team";
    void refreshHub();
}

function onCampaignCreated() {
    // AdminTab re-fetches campaigns on mount, and switching viewKey back to
    // `list:admin` remounts it — so the new campaign will appear naturally.
    transitionName.value = "hub-back";
    activeView.value = { kind: "list" };
    requestedTab.value = "admin";
}

async function onCycleCreated(payload: { cycleId: string; isSelfAssessmentOnly: boolean; selfAssessEnabled: boolean; title: string; closesAt: string | null }) {
    // Self-only cycles are auto-launched by the API on create — go straight
    // to the take flow. 360 cycles land on the DRAFT view of Nominate so the
    // subject can invite raters + launch.
    transitionName.value = "hub-forward";
    if (payload.isSelfAssessmentOnly) {
        activeView.value = { kind: "take-self", cycleId: payload.cycleId, title: payload.title, closesAt: payload.closesAt };
    } else {
        activeView.value = { kind: "nominate", cycleId: payload.cycleId };
    }
    // Refresh hub payload in the background so the list catches the new cycle
    // when the user eventually exits back to it.
    void refreshHub();
}

async function onCycleLaunched(payload: { cycleId: string; selfAssessEnabled: boolean; title: string; closesAt: string | null }) {
    // The brand-mark interstitial plays first, then the confirmation screen
    // hands the subject over to their own self-assessment.
    transitionName.value = "hub-forward";
    activeView.value = {
        kind: "launching",
        cycleId: payload.cycleId,
        title: payload.title,
        selfAssessEnabled: payload.selfAssessEnabled,
        closesAt: payload.closesAt,
    };
    void refreshHub();
}

function onLaunchAnimationDone() {
    if (activeView.value.kind !== "launching") {
        return;
    }
    const { cycleId, title, selfAssessEnabled, closesAt } = activeView.value;
    activeView.value = { kind: "launched", cycleId, title, selfAssessEnabled, closesAt };
}

function onStartSelfAfterLaunch() {
    if (activeView.value.kind !== "launched") {
        return;
    }
    const { cycleId, title, closesAt } = activeView.value;
    transitionName.value = "hub-forward";
    activeView.value = { kind: "take-self", cycleId, title, closesAt, step: "Step 3 of 3", ...SIXTY_ACCENT };
}

async function refreshHub() {
    const { data } = await useFetch("/v1/reflections/hub").json<HubPayload>();
    if (data.value) {
        hub.value = data.value;
    }
}

async function onSelfSubmitted() {
    // A self-only reflection unlocks on submit, so the user lands on the
    // results panel, which polls until the report is ready — a coherent
    // "processing → results" flow rather than a stale list with a "Generating…"
    // row. The self leg of a 360 has no results to show while raters are still
    // responding, so it gets the confirmation panel instead. Refresh the hub in
    // the background either way so the list is current when they navigate back.
    const view = activeView.value;
    transitionName.value = "hub-forward";
    if (view.kind !== "take-self") {
        activeView.value = { kind: "list" };
    } else {
        activeView.value = { kind: view.isSixty ? "self-complete" : "results", cycleId: view.cycleId };
    }
    void refreshHub();
}

const upcomingItems = computed<UpcomingItem[]>(() => {
    if (!hub.value) {
        return [];
    }
    const items: UpcomingItem[] = [];
    for (const req of hub.value.team_reflection_requests ?? []) {
        items.push({
            id: `team-${req.invite_token}`,
            kind: "team_reflection",
            title: `Give 360 feedback to ${req.subject_first_name}`,
            subtitle: `Requested by ${req.subject_first_name} · Anonymous`,
            chipLabel: "Team Review",
            ctaLabel: "Give feedback",
            href: `/reflections/360/r/${req.invite_token}/`,
            closesAt: req.closes_at ?? null,
            inviteToken: req.invite_token,
            subjectFirstName: req.subject_first_name,
        });
    }
    for (const task of hub.value.pending_personal_tasks ?? []) {
        // `task.id` is either a cycle id or a campaign id depending on kind.
        // The row keeps both fields distinct so downstream handlers know
        // whether to route through /cycles/{id} or /cycles/from-campaign.
        const kind = mapTaskKind(task.kind, task.is_self_assessment_only ?? true);
        const isCampaign = kind === "self_reflection_campaign" || kind === "cycle_setup_campaign";
        items.push({
            id: `task-${task.id}`,
            kind,
            title: task.is_self_assessment_only ? `Complete your ${task.title}` : `Finish setup for ${task.title}`,
            subtitle: task.subtitle ?? "",
            chipLabel: task.is_self_assessment_only ? "Self Reflection" : "360",
            ctaLabel: task.is_self_assessment_only ? "Start check in" : "Continue",
            href: hrefForTask({ kind: task.kind, id: task.id, is_self_assessment_only: task.is_self_assessment_only ?? true }),
            closesAt: task.closes_at ?? null,
            cycleId: isCampaign ? undefined : task.id,
            campaignId: isCampaign ? task.id : undefined,
            selfReflectionTitle: task.is_self_assessment_only ? task.title : undefined,
        });
    }
    for (const cycle of hub.value.active_360s ?? []) {
        items.push({
            id: `active-${cycle.id}`,
            kind: "active_360_status",
            title: `Your ${cycle.template.name} is collecting`,
            subtitle: "",
            chipLabel: "360",
            ctaLabel: "View",
            href: `/reflections/360/${cycle.id}`,
            closesAt: cycle.closes_at ?? null,
            progress: { responded: cycle.response_summary.responded, total: Math.max(cycle.response_summary.total, cycle.min_responses) },
        });
    }
    return items;
});

const selfTasks = computed(() => {
    if (!hub.value) {
        return [];
    }
    return (hub.value.pending_personal_tasks ?? []).filter((t) => t.is_self_assessment_only);
});

const sixtyTasks = computed(() => {
    if (!hub.value) {
        return [];
    }
    return (hub.value.pending_personal_tasks ?? []).filter((t) => !t.is_self_assessment_only);
});

// Fallback hrefs so a middle-click / new-tab still opens the full page — the
// hub UI hijacks the primary click via emits, but keeping real URLs preserves
// deep-link + browser-history behavior.
function hrefForTask(task: { kind: string; id: string; is_self_assessment_only: boolean }): string {
    if (task.kind === "self_reflection_cycle") {
        return `/reflections/360/${task.id}/self`;
    }
    if (task.kind === "self_reflection_campaign") {
        return `/reflections/360/start?mode=self&campaign_id=${task.id}`;
    }
    if (task.kind === "cycle_setup_campaign") {
        return `/reflections/360/start?campaign_id=${task.id}`;
    }
    if (task.kind === "cycle_setup") {
        return `/reflections/360/${task.id}/nominate`;
    }
    return `/reflections/360/${task.id}`;
}

// Backend `PendingPersonalTask.kind` values map onto UpcomingItem kinds. The
// mapping keeps the frontend's kind space closed (no free-form strings) so
// TypeScript catches missing branches when new kinds are added.
function mapTaskKind(kind: string, isSelfAssessmentOnly: boolean): UpcomingItem["kind"] {
    if (kind === "self_reflection_campaign") {
        return "self_reflection_campaign";
    }
    if (kind === "cycle_setup_campaign") {
        return "cycle_setup_campaign";
    }
    if (kind === "self_reflection_cycle") {
        return "self_reflection";
    }
    if (kind === "cycle_setup") {
        return "cycle_setup";
    }
    // Legacy / unknown — fall back to the closest kind that keeps navigation
    // working via the row's fallback href.
    return isSelfAssessmentOnly ? "self_reflection" : "cycle_setup";
}

async function onStartFromCampaign(payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }) {
    // Materialize a real cycle from the campaign, then route the same way
    // onCycleCreated would. Self-only campaigns are auto-launched server-side
    // (see /cycles/from-campaign) so the taker mounts immediately.
    transitionName.value = "hub-forward";
    const { data, error } = await useFetch("/v1/reflections/cycles/from-campaign")
        .post(JSON.stringify({ campaign_id: payload.campaignId }), "application/json")
        .json<CycleEnvelopeResponse>();
    if (error.value || !data.value?.cycle) {
        loadError.value = "Could not start this reflection. Please refresh and try again.";
        return;
    }
    const cycle = data.value.cycle;
    if (payload.isSelfAssessmentOnly || cycle.is_self_assessment_only) {
        activeView.value = { kind: "take-self", cycleId: cycle.id, title: payload.title, closesAt: cycle.closes_at ?? null };
    } else {
        activeView.value = { kind: "nominate", cycleId: cycle.id };
    }
    void refreshHub();
}

async function onRemind(inviteId: string) {
    const cycle = hub.value?.active_360s?.find((c) => c.invites.some((i) => i.id === inviteId));
    if (!cycle) {
        return;
    }
    await useFetch(`/v1/reflections/cycles/${cycle.id}/invites/${inviteId}/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json();
}

async function onWrapUp(cycleId: string) {
    // Server-side floor is 3 submitted rater responses; the button in
    // Active360Card enforces the same floor client-side to skip the round trip.
    const { data, error } = await useFetch(`/v1/reflections/cycles/${cycleId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json();
    if (error.value) {
        loadError.value = (data.value as { error?: string })?.error || "Could not wrap up this cycle.";
        return;
    }
    // Land on the results panel so the user sees the "Generating…" state
    // through to the finished report rather than a stale list row.
    transitionName.value = "hub-forward";
    activeView.value = { kind: "results", cycleId };
    void refreshHub();
}
</script>

<style scoped>
/* Slide-fade between hub views. `hub-forward` slides new content in from the
   right (drilling into a take/results panel), `hub-back` slides from the left
   (returning to the list). Reduced-motion users get an instant swap. */
.hub-forward-enter-active,
.hub-forward-leave-active,
.hub-back-enter-active,
.hub-back-leave-active {
    transition:
        opacity 200ms ease,
        transform 220ms cubic-bezier(0.2, 0, 0, 1);
}

.hub-forward-enter-from {
    opacity: 0;
    transform: translateX(24px);
}

.hub-forward-leave-to {
    opacity: 0;
    transform: translateX(-24px);
}

.hub-back-enter-from {
    opacity: 0;
    transform: translateX(-24px);
}

.hub-back-leave-to {
    opacity: 0;
    transform: translateX(24px);
}

@media (prefers-reduced-motion: reduce) {
    .hub-forward-enter-active,
    .hub-forward-leave-active,
    .hub-back-enter-active,
    .hub-back-leave-active {
        transition: none;
    }

    .hub-forward-enter-from,
    .hub-forward-leave-to,
    .hub-back-enter-from,
    .hub-back-leave-to {
        transform: none;
    }
}
</style>
